import { WebMercatorViewport, MapViewState } from '@deck.gl/core/typed'
import { Position } from 'src/common-services'
import { Bounds } from 'src/components/map-new/types'

const DISTANCE_DIFF_THRESHOLD = 0.0001
const ZOOM_DIFF_THRESHOLD = 0.25
const BOUNDS_DIFF_THRESHOLD_GMAP = 0.00001
const BOUNDS_VALID_DIFF = 50

const isBoundMinorThanDiff = (a: Bounds, b: Bounds, diff: number) => {
  return (
    Math.abs(a.northEast[0] - b.northEast[0]) < diff &&
    Math.abs(a.northEast[1] - b.northEast[1]) < diff &&
    Math.abs(a.southWest[0] - b.southWest[0]) < diff &&
    Math.abs(a.southWest[1] - b.southWest[1]) < diff
  )
}

export const isGMapBoundsEqual = (a: Bounds, b: Bounds) => {
  return isBoundMinorThanDiff(a, b, BOUNDS_DIFF_THRESHOLD_GMAP)
}

export const isBoundsEqual = (a: Bounds | undefined, b: Bounds | undefined) => {
  if (!a || !b) {
    return false
  }
  return isBoundMinorThanDiff(a, b, DISTANCE_DIFF_THRESHOLD)
}

export const isBoundsDistanceValid = (a: Bounds, b: Bounds) => {
  return isBoundMinorThanDiff(a, b, BOUNDS_VALID_DIFF)
}

export const isDistanceMinorThanDiff = (a: number, b: number, diff: number) => {
  return Math.abs(a - b) < diff
}

export const isDistanceEqual = (a: number | undefined, b: number | undefined) => {
  if (a === undefined || b === undefined) {
    return false
  }
  return isDistanceMinorThanDiff(a, b, DISTANCE_DIFF_THRESHOLD)
}

export const isPositionEqual = (a: Position | undefined, b: Position | undefined) => {
  if (a === undefined || b === undefined) {
    return false
  }
  return isDistanceEqual(a[0], b[0]) && isDistanceEqual(a[1], b[1])
}

export const isZoomMinorThanDiff = (a: number, b: number, diff: number) => {
  return Math.abs(a - b) < diff
}

export const isZoomEqual = (a: number | undefined, b: number | undefined) => {
  if (a === undefined || b === undefined) {
    return false
  }
  return isZoomMinorThanDiff(a, b, ZOOM_DIFF_THRESHOLD)
}

export const viewportToBounds = (viewState: MapViewState): Bounds => {
  const viewport = new WebMercatorViewport(viewState)
  const northEast = viewport.unproject([viewport.width, 0])
  const southWest = viewport.unproject([0, viewport.height])
  const center = viewport.unproject([viewport.width / 2, viewport.height / 2])

  return {
    northEast,
    southWest,
    center: center,
  }
}

export const boundsToViewport = (viewState: MapViewState, bounds: Bounds, animated: boolean = true): MapViewState => {
  const viewport = new WebMercatorViewport(viewState)
  try {
    const nw = [bounds.northEast[0], bounds.southWest[1]]
    const se = [bounds.southWest[0], bounds.northEast[1]]
    // @ts-ignore
    const { longitude, latitude, zoom } = viewport.fitBounds([nw, se], {
      padding: 20,
    })
    return {
      ...viewState,
      longitude,
      latitude,
      zoom,
      transitionDuration: animated ? 1000 : 0,
    }
  } catch (e) {
    console.error(e)
    return viewState
  }
}
